import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Warning, Info, X, FloppyDisk } from "@phosphor-icons/react";

const SEV_COLOR = {
    info: { bg: "bg-cyan-500/20", text: "text-cyan-200", border: "border-cyan-500/40", dot: "bg-cyan-400" },
    warning: { bg: "bg-amber-500/20", text: "text-amber-200", border: "border-amber-500/40", dot: "bg-amber-400" },
    critical: { bg: "bg-red-500/20", text: "text-red-200", border: "border-red-500/40", dot: "bg-red-400" },
};

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function EquipmentNotesPanel({ equipmentId, user, canEdit }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | note object

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/equipment/${equipmentId}/notes`);
            setItems(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [equipmentId]);

    useEffect(() => { load(); }, [load]);

    const removeNote = async (note) => {
        if (!window.confirm(`Delete note "${note.title}"?`)) return;
        try {
            await api.delete(`/equipment/notes/${note.id}`);
            toast.success("Note deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="p-4 sm:p-8" data-testid="equipment-notes-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-sm uppercase">Equipment Notes</div>
                    <div className="text-xs text-slate-400">Shared between all electricians, planners and admins.</div>
                </div>
                {canEdit && (
                    <button onClick={() => setEditing("new")} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5" data-testid="add-note-btn">
                        <Plus size={13} weight="bold" /> Add note
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-slate-400 py-6 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-slate-400 py-10 text-center text-sm border border-dashed border-cyan-400/20 rounded-md">No notes yet. Add the first one to share knowledge with the team.</div>
            ) : (
                <div className="space-y-2.5">
                    {items.map((n) => {
                        const sev = SEV_COLOR[n.severity] || SEV_COLOR.info;
                        const canRowEdit = canEdit && (user?.role === "admin" || user?.role === "manager" || n.author_id === user?.id);
                        return (
                            <div key={n.id} className={`rounded-md p-3 border ${sev.border} ${sev.bg}`} data-testid={`note-row-${n.id}`}>
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                        <span className={`w-2.5 h-2.5 rounded-full ${sev.dot} mt-1.5`} />
                                        <div className="flex-1 min-w-0">
                                            <div className={`font-bold ${sev.text} truncate`}>{n.title}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{n.author_name} · {n.author_role} · {timeAgo(n.created_at)}{n.updated_at !== n.created_at ? " · edited" : ""}</div>
                                        </div>
                                    </div>
                                    {canRowEdit && (
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setEditing(n)} className="p-1.5 rounded bg-white/5 hover:bg-white/15 text-slate-200" data-testid={`note-edit-${n.id}`}><PencilSimple size={12} /></button>
                                            <button onClick={() => removeNote(n)} className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300" data-testid={`note-delete-${n.id}`}><Trash size={12} /></button>
                                        </div>
                                    )}
                                </div>
                                <div className="text-sm text-slate-100 mt-2 whitespace-pre-wrap">{n.body}</div>
                                {n.tags && n.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {n.tags.map((t) => (<span key={t} className="text-[10px] uppercase tracking-wider bg-white/5 text-slate-300 px-1.5 py-0.5 rounded-sm">{t}</span>))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <NoteFormModal
                    equipmentId={equipmentId}
                    note={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function NoteFormModal({ equipmentId, note, onClose, onSaved }) {
    const [f, setF] = useState({
        title: note?.title || "",
        body: note?.body || "",
        severity: note?.severity || "info",
        tags: (note?.tags || []).join(", "),
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!f.title.trim() || !f.body.trim()) {
            toast.error("Title and body required");
            return;
        }
        const payload = {
            title: f.title.trim(),
            body: f.body.trim(),
            severity: f.severity,
            tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
        };
        try {
            setSaving(true);
            if (note) {
                await api.patch(`/equipment/notes/${note.id}`, payload);
                toast.success("Note updated");
            } else {
                await api.post(`/equipment/${equipmentId}/notes`, payload);
                toast.success("Note created");
            }
            onSaved && onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[95] bg-slate-900/85 flex items-start sm:items-center justify-center p-2 overflow-y-auto" data-testid="note-form-modal">
            <div className="bg-white rounded-lg w-full max-w-lg shadow-xl border border-slate-200 relative">
                <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="note-form-close"><X size={16} /></button>
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="uppercase-label text-[10px] text-[var(--grindrod-red)]">{note ? "Edit Note" : "New Note"}</div>
                    <h3 className="font-display font-black text-xl text-[var(--grindrod-navy)]">{note ? "Edit equipment note" : "Add equipment note"}</h3>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title">
                        <input type="text" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="g-input" data-testid="note-form-title" placeholder="e.g. VSD reset procedure" />
                    </Field>
                    <Field label="Body">
                        <textarea rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="g-input" data-testid="note-form-body" placeholder="Steps, observations, contact info…" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Severity">
                            <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} className="g-input" data-testid="note-form-severity">
                                <option value="info">Info</option>
                                <option value="warning">Warning</option>
                                <option value="critical">Critical</option>
                            </select>
                        </Field>
                        <Field label="Tags (comma-separated)">
                            <input type="text" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="g-input" data-testid="note-form-tags" placeholder="VSD, reset" />
                        </Field>
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm px-3 py-2 rounded-sm bg-slate-100 hover:bg-slate-200">Cancel</button>
                    <button onClick={submit} disabled={saving} className="text-sm px-3 py-2 rounded-sm bg-[var(--grindrod-navy)] text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="note-form-save">
                        <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">{label}</span>
            {children}
        </label>
    );
}
